import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { useEffect, useMemo } from 'react';
import { Pagination } from '../../sdk';

export interface PaginatedResponse<T> {
  results: T[];
  pagination: Pagination;
}

const getNextPageParam = <T>(lastPage: PaginatedResponse<T>) => {
  const { limit, offset, totalResults } = lastPage.pagination;
  if (offset + limit < totalResults) {
    return { limit, offset: offset + limit };
  }
  return false;
};

export const useFetchAllPages = <T>(
  options: Omit<UseInfiniteQueryOptions<PaginatedResponse<T>, unknown>, 'getNextPageParam'>,
) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } = useInfiniteQuery({
    ...options,
    getNextPageParam,
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const aggregatedData = useMemo(() => data?.pages.flatMap((page) => page.results), [data?.pages]);

  return {
    ...rest,
    data: aggregatedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
